import { useEffect, useState } from "react";
import { QrCode } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useVCardStore } from "../../store/vcardStore";
import { useAuthStore } from "../../store/authStore";
import type { VCardCategory } from "../../types/vcard";
import type { SnowGearSubCategory } from "../../types/snowGear";
import { FileNaming } from "../../utils/UniversalFileNaming";
import { QRCodeService } from "../../services/qrCodeService";

interface QRCodePreviewProps {
  category: VCardCategory;
  subCategory?: SnowGearSubCategory;
  size?: number;
  compact?: boolean;
}

const QRCodePreview = ({
  category,
  subCategory,
  size = 256,
  compact = false,
}: QRCodePreviewProps) => {
  const user = useAuthStore((state) => state.user);
  const vcards = useVCardStore((state) => state.vcards);

  // Find active vCard for the category (needed by useEffect below)
  const activeVCard =
    vcards.find(
      (v) => v.category === category && v.userId === user?.id && v.isActive,
    ) || vcards.find((v) => v.category === "home" && v.userId === user?.id);

  const [tokenUrl, setTokenUrl] = useState<string | null>(null);

  useEffect(() => {
    setTokenUrl(null);
    if (!activeVCard?.id) return;
    let cancelled = false;
    QRCodeService.getTokenUrl(activeVCard.id, user?.id)
      .then((url) => {
        if (!cancelled && url) setTokenUrl(url);
      })
      .catch(() => {
        /* fall back to legacy URL */
      });
    return () => {
      cancelled = true;
    };
  }, [activeVCard?.id]);

  if (!user) return null;

  const legacyUrl = `${window.location.origin}/qr/${category}/${user.id}${subCategory ? `/${subCategory}` : ""}`;
  const qrValue = tokenUrl ?? legacyUrl;

  const handleDownload = () => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = 1024;
    canvas.height = 1024;

    const tempSvg = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "svg",
    );
    tempSvg.setAttribute("width", "1024");
    tempSvg.setAttribute("height", "1024");
    tempSvg.innerHTML = document.querySelector(".qr-code-svg")?.innerHTML || "";

    const svgData = new XMLSerializer().serializeToString(tempSvg);
    const svgBlob = new Blob([svgData], {
      type: "image/svg+xml;charset=utf-8",
    });
    const url = URL.createObjectURL(svgBlob);
    const [fallbackFirstName = "", fallbackLastName = ""] = (
      user.name ?? ""
    ).split(" ");

    // Generate filename using UniversalFileNaming
    const fileName = FileNaming.generate("qrcode", {
      userId: user.id,
      firstName: activeVCard?.firstName || fallbackFirstName,
      lastName: activeVCard?.lastName || fallbackLastName,
      category,
      item: subCategory || "default",
      timestamp: new Date(),
    }).replace(".svg", ".png");

    const img = new window.Image();
    img.onload = () => {
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      const pngUrl = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.download = fileName;
      link.href = pngUrl;
      link.click();

      URL.revokeObjectURL(url);
    };
    img.src = url;
  };

  return (
    <div className="flex flex-col items-center">
      <div className="bg-white p-4 rounded-lg shadow-sm">
        <QRCodeSVG
          value={qrValue}
          size={size}
          level="H"
          includeMargin
          className="qr-code-svg"
        />
      </div>

      <p
        className={`text-sm text-gray-600 dark:text-gray-300 ${compact ? "mt-2" : "mt-4"} text-center`}
      >
        {activeVCard?.isActive
          ? `Scan to view ${subCategory ? subCategory.replace(/([A-Z])/g, " $1").toLowerCase() : category} information`
          : "Currently displaying home contact information"}
      </p>

      <button
        onClick={handleDownload}
        className={`flex items-center space-x-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 px-3 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-sm text-gray-900 dark:text-gray-100 ${compact ? "mt-2" : "mt-4"}`}
      >
        <QrCode className="h-4 w-4" />
        <span>Download QR</span>
      </button>
    </div>
  );
};

export default QRCodePreview;
